import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Button,
  Grid,
  Chip,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';

interface QueueStatus {
  processor_running: boolean;
  concurrency: number;
  total_items: number;
  pending_items: number;
  processing_items: number;
  completed_items: number;
  failed_items: number;
  progress_percent: number;
}

const ProgressWrapper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
}));

export default function ProgressBar() {
  const [status, setStatus] = useState<QueueStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = async () => {
    try {
      const response = await fetch('/api/validation/queue/status');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setStatus(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startProcessor = async () => {
    setActionLoading(true);
    try {
      const response = await fetch('/api/validation/queue/start-processor?concurrency=5', {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      await fetchStatus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const stopProcessor = async () => {
    setActionLoading(true);
    try {
      const response = await fetch('/api/validation/queue/stop-processor', {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      await fetchStatus();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !status) {
    return (
      <ProgressWrapper>
        <LinearProgress />
      </ProgressWrapper>
    );
  }

  if (error) {
    return (
      <ProgressWrapper>
        <Alert severity="error">{error}</Alert>
      </ProgressWrapper>
    );
  }

  if (!status) return null;

  return (
    <ProgressWrapper>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold">
          Queue Processing Progress
        </Typography>
        <Box>
          <Chip
            label={status.processor_running ? 'Running' : 'Stopped'}
            color={status.processor_running ? 'success' : 'default'}
            sx={{ mr: 2 }}
          />
          <Chip
            label={`Concurrency: ${status.concurrency}`}
            variant="outlined"
            size="small"
          />
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">
            {status.pending_items + status.processing_items} items remaining
          </Typography>
          <Typography variant="body2" fontWeight="600">
            {status.progress_percent.toFixed(1)}% complete
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={status.progress_percent}
          sx={{ height: 10, borderRadius: 5 }}
        />
      </Box>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={6} sm={3}>
          <Box textAlign="center">
            <Typography variant="h6" fontWeight="bold" color="text.secondary">
              {status.pending_items}
            </Typography>
            <Typography variant="body2" color="text.secondary">Pending</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box textAlign="center">
            <Typography variant="h6" fontWeight="bold" color="warning.main">
              {status.processing_items}
            </Typography>
            <Typography variant="body2" color="text.secondary">Processing</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box textAlign="center">
            <Typography variant="h6" fontWeight="bold" color="success.main">
              {status.completed_items}
            </Typography>
            <Typography variant="body2" color="text.secondary">Completed</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box textAlign="center">
            <Typography variant="h6" fontWeight="bold" color="error.main">
              {status.failed_items}
            </Typography>
            <Typography variant="body2" color="text.secondary">Failed</Typography>
          </Box>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 3 }}>
        {!status.processor_running ? (
          <Button
            variant="contained"
            onClick={startProcessor}
            disabled={actionLoading}
          >
            {actionLoading ? 'Starting...' : 'Start Processor'}
          </Button>
        ) : (
          <Button
            variant="outlined"
            color="error"
            onClick={stopProcessor}
            disabled={actionLoading}
          >
            {actionLoading ? 'Stopping...' : 'Stop Processor'}
          </Button>
        )}
      </Box>
    </ProgressWrapper>
  );
}
